import UrlFormatter from "../../../app/formatters/UrlFormatter";
import { parse, format } from "date-fns";

/** @typedef {import('../models/RpaResourceModel.js').RpaResourceItem } RpaResourceItem */
/** @typedef {import('../models/RpaScheduleGridModel.js').RpaScheduleGridItem } RpaScheduleGridItem */

/**
 * Retourne l'URL d'accès à la page de planning des transports
 * @param {string} dayDateStr - Date de recherche au format string DD/MM/YYYY
 * @returns {string} - URL d'accès à la page de planning des transports
 */
export function getRpaTransportUrl(dayDateStr) {
  return `/rpa-transport/_planning?dayDateStr=${dayDateStr}&sidebarreduce=true`;
}

/**
 * Retourne l'URL d'accès à la page de consultation des réservations d'une resource et d'une plage horaire si il y a une plage horaire
 * @param {string} dayDateStr - Date de recherche au format string DD/MM/YYYY
 * @param {RpaResourceItem} rpaResource - Ressource
 * @param {RpaScheduleGridItem} [rpaScheduleGrid] - Plage horaire
 * @returns {string} - URL d'accès à la page de consultation des réservations d'une plage horaire
 */
export function getShowHrefUrl(dayDateStr, rpaResource, rpaScheduleGrid) {
  const formattedDateDateStr = format(parse(dayDateStr, "dd/MM/yyyy", new Date()), "yyyy-MM-dd");

  return rpaScheduleGrid ? `/rpa-reservation/_showresas/${formattedDateDateStr}?rpaScheduleGridId=${rpaScheduleGrid.id}&rpaResourceId=${rpaResource.id}&sidebarreduce=true` : `/rpa-reservation/_showresas/${formattedDateDateStr}?rpaResourceId=${rpaResource.id}&sidebarreduce=true`;
}

/**
 * Retourne l'URL d'accès au lancement du live
 * @param {string} dayDateStr - Date de recherche au format string DD/MM/YYYY
 * @param {RpaResourceItem} rpaResource - Ressource
 * @returns {string} - URL d'accès au lancement du live
 */
export function getLocationUrl(dayDateStr, rpaResource) {
  return `/rpa-location/_stock/${rpaResource.id}?sidebarreduce=true&dayDateStr=${dayDateStr}&forcereturnurl=/rpa/_index?dayDateStr=${dayDateStr}`;
}

/**
 * Retourne l'URL d'accès à la page d'ajout de réservation front ou back
 * @param {string} view - Vue back ou front
 * @param {string} dayDateStr - Date de recherche au format string DD/MM/YYYY
 * @param {RpaResourceItem} rpaResource - Ressource
 * @param {RpaScheduleGridItem} rpaScheduleGrid - Plage horaire
 * @param {boolean} transportAndParkingOption - Gestion des transports et stationnements
 * @param {?string} lang - Langue
 * @param {?string} source - Source
 * @returns {string} - URL d'accès à la page d'ajout de réservation front ou back
 */
export function getAddHrefUrl(view, dayDateStr, rpaResource, rpaScheduleGrid, transportAndParkingOption, lang, source) {
  var url, onlyFields, returnUrl;

  if (view === "back") {
    onlyFields = "comment,firstname,lastname,nb_peoples,live_api,sync_in_agenda,userId,nb_options";

    if (transportAndParkingOption) {
      onlyFields += ",rpaATransportId,rpaBTransportId,rpaLocationId";
    }

    if (rpaResource.rpa_options.length) {
      onlyFields += ",rpaOptionId";
    }

    if (rpaResource.rpa_locations.length) {
      url = `/_add/rpa-reservation?onlyfields=${onlyFields}&sidebarreduce=true&rpaResourceId=${rpaResource.id}&rpaScheduleGridId=${rpaScheduleGrid.id}&date=${dayDateStr}&rpaServiceId=&rpaUserServiceId=&subIncludingProperty=rpaResourceId&includingId=${rpaResource.id}`;
    } else {
      url = `/_add/rpa-reservation?onlyfields=${onlyFields}&sidebarreduce=true&rpaResourceId=${rpaResource.id}&rpaScheduleGridId=${rpaScheduleGrid.id}&date=${dayDateStr}&rpaServiceId=&rpaUserServiceId=&subIncludingProperty=rpaActivityId&includingId=${rpaResource.rpaActivityId}`;
    }

    if (!transportAndParkingOption) {
      url += "&rpaLocationId=";
    }
    url += `&forcereturnurl=/rpa/_index?dayDateStr=${dayDateStr}`;
  } else {
    returnUrl = window.location.pathname + window.location.search + window.location.hash;
    url = `/rpa-reservation/__add?rpaResourceId=${rpaResource.id}&rpaScheduleGridId=${rpaScheduleGrid.id}&date=${dayDateStr}&returnurl=${encodeURIComponent(returnUrl)}`;
    url = UrlFormatter.addUrlLangAndSource(url, lang, source);
  }

  return url;
}

/**
 * Retourne l'url de fermeture d'une plage horaire
 * @param {string} dayDateStr - Date de recherche au format string DD/MM/YYYY
 * @param {RpaResourceItem} rpaResource - Ressource
 * @param {RpaScheduleGridItem} rpaScheduleGrid - Plage horaire
 * @returns {string} - URL de fermeture d'une plage horaire
 */
export function getCloseUrl(dayDateStr, rpaResource, rpaScheduleGrid) {
  return `/rpa-info-schedule-grid/_closed/${rpaScheduleGrid.id}/${rpaResource.id}?date=${dayDateStr}&forcereturnurl=/rpa/_index?dayDateStr=${dayDateStr}`;
}

/**
 * Retourne l'url de réouverture d'une plage horaire
 * @param {string} dayDateStr - Date de recherche au format string DD/MM/YYYY
 * @param {RpaResourceItem} rpaResource - Ressource
 * @param {RpaScheduleGridItem} rpaScheduleGrid - Plage horaire
 * @returns {string} - URL de fermeture d'une plage horaire
 */
export function getReOpenUrl(dayDateStr, rpaResource, rpaScheduleGrid) {
  return `/rpa-info-schedule-grid/_open/${rpaScheduleGrid.id}/${rpaResource.id}?date=${dayDateStr}&forcereturnurl=/rpa/_index?dayDateStr=${dayDateStr}`;
}

/**
 * Retourne l'url de validation d'une plage horaire
 * @param {string} dayDateStr - Date de recherche au format string DD/MM/YYYY
 * @param {RpaResourceItem} rpaResource - Ressource
 * @param {RpaScheduleGridItem} rpaScheduleGrid - Plage horaire
 * @returns {string} - URL de validation d'une plage horaire
 */
export function getValidateUrl(dayDateStr, rpaResource, rpaScheduleGrid) {
  return `/rpa-info-schedule-grid/_checked/${rpaScheduleGrid.id}/${rpaResource.id}?date=${dayDateStr}&forcereturnurl=/rpa/_index?dayDateStr=${dayDateStr}`;
}
